import { useMemo } from 'react'
import { CUSTOMER_MESSAGES } from '../../../constants/customerMessages'
import { useSelector } from 'react-redux'
import { selectHomeId } from '../../../store/selectors/home'
import * as Sentry from '@sentry/browser'
import { usePrequalRequest } from '../../usePrequalRequest'
import { useVerificationFlow } from '../../../Components/shared/VerificationFlow'
import { Listing } from '../../../api/gqlaxy/gql/generated/graphql'
import {
  selectCompanyLogo,
  selectCustomerFirstName,
  selectCustomerLastName,
  selectCustomerPhotoUri
} from '../../../store/selectors/customerProfile'

export const useSendPrequalStep = (listing?: Listing) => {
  const { sendPrequal, sendPrequalForListing, isLoading: isSendingPrequal } = usePrequalRequest()
  const custFirstName = useSelector(selectCustomerFirstName)
  const custLastName = useSelector(selectCustomerLastName)
  const custPhotoUri = useSelector(selectCustomerPhotoUri)
  const corpPhotoUri = useSelector(selectCompanyLogo)
  const { state, dispatch: dispatchVerificationFlow } = useVerificationFlow()
  const membership = state.loanOfficerMembership

  const homeId = useSelector(selectHomeId)
  const topicKey = useMemo(() => {
    if (state.customerIsLoanOfficer) {
      return CUSTOMER_MESSAGES.HOMEOWNER_PREQUAL_REQUEST.topicKey
    }

    if (homeId) {
      return CUSTOMER_MESSAGES.HBN_HOMEOWNER_PREQUAL_REQUEST.topicKey
    }

    return CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.topicKey
  }, [state.customerIsLoanOfficer, homeId])

  const topic = useMemo(() => {
    if (state.customerIsLoanOfficer) {
      return CUSTOMER_MESSAGES.HOMEOWNER_PREQUAL_REQUEST.topic
    }

    if (homeId) {
      return CUSTOMER_MESSAGES.HBN_HOMEOWNER_PREQUAL_REQUEST.topic
    }

    return CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.topic
  }, [state.customerIsLoanOfficer, homeId])

  const message = useMemo(() => {
    if (state.message) {
      return state.message
    }

    if (state.customerIsLoanOfficer) {
      return CUSTOMER_MESSAGES.HOMEOWNER_PREQUAL_REQUEST.message.defaultMessage
    }

    if (homeId) {
      return CUSTOMER_MESSAGES.HBN_HOMEOWNER_PREQUAL_REQUEST.message.defaultMessage
    }

    return CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.message.defaultMessage
  }, [state.message, state.customerIsLoanOfficer, homeId])

  /**
   * If a listing is provided, send the prequal request for the listing.
   * Otherwise, send the normal prequal request.
   */
  const onSubmit = async () => {
    try {
      if (listing && state.customerIsLoanOfficer) {
        await sendPrequalForListing({ listing })
      } else {
        await sendPrequal({
          topic,
          topicKey,
          message
        })
      }
      dispatchVerificationFlow({ type: 'PREQUAL_SENT' })
    } catch (error) {
      Sentry.captureException(error)
      throw error
    }
  }

  const onCancel = () => {
    dispatchVerificationFlow({ type: 'CANCEL' })
  }

  const customerFirstName = useMemo(() => {
    if (state.customerIsLoanOfficer) {
      return custFirstName
    }
    return membership?.customerProfile?.firstName || ''
  }, [state.customerIsLoanOfficer, custFirstName, membership])

  const customerLastName = useMemo(() => {
    if (state.customerIsLoanOfficer) {
      return custLastName
    }
    return membership?.customerProfile?.lastName || ''
  }, [state.customerIsLoanOfficer, custLastName, membership])

  const customerPhotoUri = useMemo(() => {
    if (state.customerIsLoanOfficer) {
      return custPhotoUri
    }
    return membership?.customerProfile?.photoUri || ''
  }, [state.customerIsLoanOfficer, custPhotoUri, membership])

  const corporatePhotoUri = useMemo(() => {
    if (state.customerIsLoanOfficer) {
      return corpPhotoUri
    }
    return membership?.corporateProfile?.avatarUri || ''
  }, [state.customerIsLoanOfficer, corpPhotoUri, membership])

  return {
    onSubmit,
    isLoading: isSendingPrequal,
    onCancel,
    customerFirstName,
    customerLastName,
    customerPhotoUri,
    corporatePhotoUri
  }
}
